import { useState, useCallback, useRef, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useFolderTree, useCreateFolder, type Folder } from './hooks/useDocuments';

export interface FolderSelectContext {
  /** The owning client's id, when the selected folder belongs to a client. */
  clientId?: string | null;
  /** True when the selected node is a client root folder (filter by client). */
  isClientRoot?: boolean;
}

interface FolderTreeProps {
  activeFolderId: string | null;
  onFolderSelect: (folderId: string | null, context?: FolderSelectContext) => void;
}

interface FolderNodeProps {
  folder: Folder;
  depth: number;
  activeFolderId: string | null;
  onFolderSelect: (folderId: string | null, context?: FolderSelectContext) => void;
  expandedIds: Set<string>;
  onToggle: (folderId: string) => void;
}

/**
 * Recursive folder node component with expand/collapse and keyboard navigation.
 */
function FolderNode({
  folder,
  depth,
  activeFolderId,
  onFolderSelect,
  expandedIds,
  onToggle,
}: FolderNodeProps) {
  const isExpanded = expandedIds.has(folder.id);
  const isActive = activeFolderId === folder.id;
  const hasChildren = folder.children && folder.children.length > 0;
  const nodeRef = useRef<HTMLButtonElement>(null);

  // A client root folder owns a client and has no parent — render it as the
  // client entry (name from the resolved clientName, falling back to folder name).
  const isClientRoot = !!folder.clientId && !folder.parentId;
  const label = isClientRoot ? folder.clientName || folder.name : folder.name;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          if (hasChildren && !isExpanded) {
            onToggle(folder.id);
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (isExpanded) {
            onToggle(folder.id);
          }
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          onFolderSelect(folder.id, { clientId: folder.clientId, isClientRoot });
          break;
      }
    },
    [folder.id, folder.clientId, isClientRoot, hasChildren, isExpanded, onToggle, onFolderSelect],
  );

  return (
    <div role="treeitem" aria-expanded={hasChildren ? isExpanded : undefined}>
      <button
        ref={nodeRef}
        type="button"
        onClick={() => onFolderSelect(folder.id, { clientId: folder.clientId, isClientRoot })}
        onKeyDown={handleKeyDown}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors
          ${
            isActive
              ? 'bg-brand-50 text-brand-700 font-medium dark:bg-brand-900/30 dark:text-brand-300'
              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
          }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        aria-current={isActive ? 'true' : undefined}
      >
        {/* Expand/collapse chevron */}
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(folder.id);
            }}
            className="flex h-4 w-4 items-center justify-center rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
          >
            <svg
              className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <span className="h-4 w-4" />
        )}

        {/* Icon: a person glyph for client roots, a folder for subfolders */}
        {isClientRoot ? (
          <svg
            className="h-4 w-4 text-brand-500 dark:text-brand-400"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg
            className="h-4 w-4 text-gray-400 dark:text-gray-500"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
        )}

        <span className={`truncate ${isClientRoot ? 'font-medium' : ''}`}>{label}</span>
      </button>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div role="group">
          {folder.children.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              depth={depth + 1}
              activeFolderId={activeFolderId}
              onFolderSelect={onFolderSelect}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Folder tree navigation component with nested expand/collapse,
 * keyboard navigation, and create folder action.
 * Validates: Requirements 9.16
 */
export function FolderTree({ activeFolderId, onFolderSelect }: FolderTreeProps) {
  const { can } = usePermissions();
  const { data: folders, isLoading } = useFolderTree();
  const createFolder = useCreateFolder();

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const handleToggle = useCallback((folderId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) return;

    await createFolder.mutateAsync({
      name: newFolderName.trim(),
      parentId: activeFolderId,
    });

    setNewFolderName('');
    setIsCreating(false);
  }, [newFolderName, activeFolderId, createFolder]);

  const handleCreateKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCreateFolder();
      } else if (e.key === 'Escape') {
        setIsCreating(false);
        setNewFolderName('');
      }
    },
    [handleCreateFolder],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Folders</h3>
        {can(PERMISSIONS.DOCUMENTS_MANAGE_FOLDERS) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCreating(true)}
            aria-label="Create new folder"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </Button>
        )}
      </div>

      {/* Create folder input */}
      {isCreating && (
        <div className="border-b border-gray-200 px-4 py-2 dark:border-gray-700">
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={handleCreateKeyDown}
            placeholder="Folder name..."
            autoFocus
            className="text-sm"
          />
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || createFolder.isPending}
            >
              Create
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsCreating(false);
                setNewFolderName('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Tree content */}
      <div className="flex-1 overflow-y-auto px-2 py-2" role="tree" aria-label="Folder navigation">
        {/* All Documents root */}
        <button
          type="button"
          onClick={() => onFolderSelect(null)}
          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors
            ${
              activeFolderId === null
                ? 'bg-brand-50 text-brand-700 font-medium dark:bg-brand-900/30 dark:text-brand-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
        >
          <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
              clipRule="evenodd"
            />
          </svg>
          <span>All Documents</span>
        </button>

        {/* Loading state */}
        {isLoading && (
          <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
            Loading folders...
          </div>
        )}

        {/* Folder tree */}
        {folders?.map((folder) => (
          <FolderNode
            key={folder.id}
            folder={folder}
            depth={0}
            activeFolderId={activeFolderId}
            onFolderSelect={onFolderSelect}
            expandedIds={expandedIds}
            onToggle={handleToggle}
          />
        ))}
      </div>
    </div>
  );
}
